import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import { Typography, TextField } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { NumericFormat } from "react-number-format";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { Snackbar, Alert } from '@mui/material';
import { IconButton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

import "./EditReception.css";

const ReceptionEdit = () => {
  const { id: receptionId } = useParams();
  const [reception, setReception] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  
  //state-uri pentru istoric
  const [historyByDate, setHistoryByDate] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingHistory, setEditingHistory] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [, setUploadProgress] = useState({});
  
  //state-uri pentru dialogul de confirmare pentru stergerea unui document
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  //state pentru adaugarea notei de receptie
  const [receptionNote, setReceptionNote] = useState('');

  const navigate = useNavigate();

  //utilitare
  const normalizeDecimal = (value) => {
    if(value === null || value === undefined || value === "") return "";
    const number = parseFloat(value);
    if(isNaN(number)) return "";

    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    }).format(number);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'received': 'received',
      'partially received': 'partially-received',
      'not received': 'not-received'
    };
    return statusMap[status?.toLowerCase()] || status?.toLowerCase().replace(' ', '-');
  };

  //grupare istoric
  const groupHistoryByDateAndTime = (historyArray) => {
    const grouped = {};
    
    const sortedHistory = [...historyArray].sort((a, b) => 
      new Date(a.change_date) - new Date(b.change_date)
    );
    
    sortedHistory.forEach(entry => {
      const dateObj = new Date(entry.change_date);
      const dateKey = dateObj.toLocaleDateString('en-EN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      const timeKey = dateObj.toLocaleTimeString('ro-RO', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      
      const dateTimeKey = `${dateKey} - ${timeKey}`;
      
      if (!grouped[dateTimeKey]) {
        grouped[dateTimeKey] = [];
      }
      
      grouped[dateTimeKey].push(entry);
    });
    
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        const dateA = new Date(a.split(' - ')[0] + ' ' + a.split(' - ')[1].replace(/\./g, ':'));
        const dateB = new Date(b.split(' - ')[0] + ' ' + b.split(' - ')[1].replace(/\./g, ':'));
        return dateB - dateA;
      })
      .forEach(key => {
        sortedGrouped[key] = grouped[key];
      });
    
    return sortedGrouped;
  };

  const calculateTotalReceived = (productId) => {
    let total = 0;
    Object.values(historyByDate).forEach(dateTimeEntries => {
      dateTimeEntries.forEach(entry => {
        if (entry.product_id === productId) {
          total += parseFloat(entry.qty_received);
        }
      });
    });
    return total;
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    const numericValue = parseFloat(value) || 0;

    if (field === "current_received") {
      newItems[index].current_received = numericValue;
    }

    setItems(newItems);
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  const validateItems = () => {
    return items.every(item => {
      const totalAfterCurrent = item.qty_received + (item.current_received || 0);
      return totalAfterCurrent <= Number(item.qty_ordered);
    });
  };

  //functii pentru istoric
  const loadAllItemsHistory = useCallback(async (itemsList) => {
    setHistoryLoading(true);
    let combinedHistory = [];

    try {
      for (const item of itemsList) {
        try {
          const url = `/reception-item/${item.id}/history/`;
          const response = await axiosInstance.get(url);

          const historyWithProductName = response.data.history.map((entry, index) => ({
            ...entry,
            product_name: item.product_name,
            product_id: item.id,
            qty_ordered: item.qty_ordered,
            qty_received: parseFloat(entry.qty_received),
            remaining_to_deliver: parseFloat(entry.remaining_to_deliver),
            timestamp: new Date(entry.change_date).getTime(),
            entry_index: index
          }));

          combinedHistory = [...combinedHistory, ...historyWithProductName];
        } catch (error) {
          console.error(`Error loading history for item ${item.id}:`, error);
        }
      }

      combinedHistory.sort((a, b) => {
        const dateCompare = a.timestamp - b.timestamp;
        if (dateCompare !== 0) return dateCompare;
        return a.entry_index - b.entry_index;
      });

      const groupedHistory = groupHistoryByDateAndTime(combinedHistory);
      setHistoryByDate(groupedHistory);

    } catch (error) {
      console.error("Error loading items history:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleStartEdit = (entry) => {
    setEditingHistory(entry);
    setEditValue(entry.qty_received);
  };

  const handleSaveHistoryEdit = async() => {
    if(!editingHistory) return;

    const newValue = parseFloat(editValue);
    if(isNaN(newValue) || newValue < 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid positive number', 
        severity: 'error'
      });
      return;
    }

    try {
      setHistoryLoading(true);
      await axiosInstance.patch(`/reception-history/${editingHistory.id}/`, {
        qty_received: newValue
      });
      await refreshData();
      setSnackbar({
        open: true,
        message: 'History entry updated successfully',
        severity: 'success'
      });
      setEditingHistory(null);
    } catch (error) {
      console.error('Error updating history:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail ||
                          'Error updating history entry';
      setSnackbar({
        open: true, 
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingHistory(null);
    setEditValue('');
  };

  //functii pentru documente
  const loadExistingDocuments = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/reception/${receptionId}/documents/`);
      setExistingDocuments(response.data);
    } catch (error) {
      console.error("Error loading documents:", error);
    }
  }, [receptionId]);

  const uploadDocuments = async () => {
    if (uploadedDocuments.length === 0) return;

    const formData = new FormData();
    uploadedDocuments.forEach(doc => {
      formData.append('documents', doc);
    });

    try {
      const response = await axiosInstance.post(
        `/reception/${receptionId}/upload-documents/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress({ percent: percentCompleted });
          },
        }
      );

      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some files failed to upload:', response.data.errors);
      }

      await loadExistingDocuments();
      setUploadedDocuments([]);
      
      return response.data;
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  };


  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axiosInstance.get(`/reception-documents/${documentId}/download/`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download document',
        severity: 'error'
      });
    }
  };

  //functie pentru deschiderea dialogului de confirmare stergere
  const handleDeleteClick = (documentId) => {
    setDocumentToDelete(documentId);
    setDeleteDialogOpen(true)
  };

  //functie pentru confirmarea stergerii
  const handleConfirmDelete = async() => {
    if(!documentToDelete) return;

    try {
      await axiosInstance.delete(`/reception-documents/${documentToDelete}/`);
      setExistingDocuments(prev => prev.filter(doc => doc.id !== documentToDelete));
      setSnackbar({
        open: true,
        message: 'Document deleted succesfully', 
        security: 'success'
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting document', 
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  //functie pentru anularea stergerii
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }

  //configurare dropzone
  const onDrop = useCallback((acceptedFiles) => {
    setUploadedDocuments(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const removeUploadedDocument = (index) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  };

    const handleReceptionNoteChange = (event) => {
    setReceptionNote(event.target.value);
  };
  
  //useEffect pentru incarcare date
  useEffect(() => {
    if (!receptionId) return;

    const loadData = async () => {
      try {
        const receptionRes = await axiosInstance.get(`/reception/${receptionId}/`);
        setReception(receptionRes.data.reception);
        
        // Set the reception note from the API response
        setReceptionNote(receptionRes.data.reception.reception_note || '');

        const initializedItems = receptionRes.data.items.map(item => {
          const qtyReceived = Number(item.qty_received);
          const remainingToDeliver = parseFloat((Number(item.qty_ordered) - qtyReceived).toFixed(4));

          return {
            ...item,
            qty_received: qtyReceived,
            remaining_to_deliver: remainingToDeliver, 
            current_received: remainingToDeliver
          };
        });

        setItems(initializedItems);
        
        await Promise.all([
          loadAllItemsHistory(initializedItems),
          loadExistingDocuments()
        ]);
      } catch (err) {
        console.error("Error loading reception:", err);
      }
    };

    loadData();
  }, [receptionId, loadAllItemsHistory, loadExistingDocuments]);

  //functie de refresh
  const refreshData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/reception/${receptionId}/`);
      setReception(response.data.reception);

      const initializedItems = response.data.items.map(item => {
        const qtyReceived = Number(item.qty_received);
        const remainingToDeliver = parseFloat((Number(item.qty_ordered) - qtyReceived).toFixed(4));
        
        return {
          ...item,
          qty_received: qtyReceived,
          remaining_to_deliver: remainingToDeliver,
          current_received: remainingToDeliver
        };
      });

      setItems(initializedItems);
      
      await Promise.all([
        loadAllItemsHistory(initializedItems),
        loadExistingDocuments()
      ]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  }, [receptionId, loadAllItemsHistory, loadExistingDocuments]);
 
  //useEffect pentru incarcare date
  useEffect(() => {
    if (!receptionId) return;

    const loadData = async () => {
      try {
        const receptionRes = await axiosInstance.get(`/reception/${receptionId}/`);
        setReception(receptionRes.data.reception);

        const initializedItems = receptionRes.data.items.map(item => {
          const qtyReceived = Number(item.qty_received);
          const remainingToDeliver = parseFloat((Number(item.qty_ordered) - qtyReceived).toFixed(4));

          return {
            ...item,
            qty_received: qtyReceived,
            remaining_to_deliver: remainingToDeliver, 
            current_received: remainingToDeliver
          };
        });

        setItems(initializedItems);
        
        await Promise.all([
          loadAllItemsHistory(initializedItems),
          loadExistingDocuments()
        ]);
      } catch (err) {
        console.error("Error loading reception:", err);
      }
    };

    loadData();
  }, [receptionId, loadAllItemsHistory, loadExistingDocuments]);

  //useEffect pentru calcul remaining_to_deliver
  const currentReceivedString = items.map(item => item.current_received).join(',');

  useEffect(() => {
    setItems(prevItems => 
      prevItems.map(item => {
        const newRemaining = parseFloat(
          Math.max(0, Number(item.qty_ordered) - (Number(item.qty_received) + (Number(item.current_received) || 0)))
            .toFixed(4)
        );
        
        return {
          ...item,
          remaining_to_deliver: newRemaining
        };
      })
    );
  }, [currentReceivedString]);

  //functia handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateItems()) {
      setOpenDialog(true);
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.put(`/reception/${receptionId}/`, { 
          status: reception.status,
          reception_note: receptionNote
        });

      if (uploadedDocuments.length > 0) {
        await uploadDocuments();
      }

      for (const item of items) {
        if (item.current_received && item.current_received > 0) {
          const historyData = {
            reception_item: item.id,
            qty_received: item.current_received
          };
          await axiosInstance.post(`/reception-history/`, historyData);
        }
      }

      await refreshData();
      
      setSnackbar({
        open: true,
        message: 'Changes saved successfully',
        severity: 'success'
      });

      setTimeout(() => {
        navigate(-1);
      }, 1000);
      
    } catch (error) {
      console.error("Error updating reception:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || 'Error saving changes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!reception) return <p>Loading reception...</p>;

  return (
    <div className="edit-reception">
      <div className="edit-reception-container">
        <div className="reception-header">
          <ArrowBackIcon className='back-arrow' onClick={() => navigate(-1)} />
          <h2 className='edit-reception-title'>Reception</h2>
          <span className={`reception-status-badge reception-status-${getStatusClass(reception.status)}`}>
            {reception.status || 'Unknown'}
          </span>
        </div>

        <hr className="title-separator" />

        <form onSubmit={handleSubmit} className="add-reception-form">
          <div className="po-button-container">
            <h3>PO NUMBER: {reception.po_number}</h3>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>

          <div className="reception-layout">
            <div className="row-1">
              <div className='items-card'>
                <h4>ITEMS</h4>
                <div className="table-container">
                  <table className="reception-table">
                    <thead>
                      <tr>
                        <th>ITEM</th>
                        <th>QTY ORDERED</th>
                        <th>TOTAL RECEIVED</th>
                        <th>CURRENT QTY RECEIVED</th>
                        <th>REMAINING TO DELIVER</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const totalReceived = calculateTotalReceived(item.id);
                        return (
                          <tr key={item.id}>
                            <td>{item.product_name}</td>
                            <td>
                              <NumericFormat
                                value={normalizeDecimal(item.qty_ordered)}
                                displayType="text"
                                decimalScale={4}
                                fixedDecimalScale={false}
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </td>
                            <td>
                              <NumericFormat
                                value={normalizeDecimal(totalReceived)}
                                displayType="text"
                                decimalScale={4}
                                fixedDecimalScale={false}
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </td>
                            <td>
                              <NumericFormat
                                value={normalizeDecimal(item.current_received || 0)}
                                allowNegative={false}
                                decimalScale={4}
                                fixedDecimalScale={false}
                                thousandSeparator="."
                                decimalSeparator=","
                                customInput="input"
                                className="qty-input"
                                onValueChange={(values) =>
                                  handleItemChange(index, "current_received", values.floatValue ?? 0)
                                }
                              />
                            </td>
                            <td>
                              <NumericFormat
                                value={normalizeDecimal(item.remaining_to_deliver)}
                                displayType="text"
                                decimalScale={4}
                                fixedDecimalScale={false}
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className='attach-card'>
                <h4>ATTACH DOCUMENTS</h4>
                <div className="attach-content">
                  <div
                    {...getRootProps()}
                    className={`dropzone-area ${isDragActive ? 'drag-active' : ''}`}
                  >
                    <input {...getInputProps()} />
                    <AttachFileIcon color='primary' fontSize="small" />
                    <Typography variant="body2" color="textSecondary">
                      {isDragActive ? 'Drop files here' : 'Drag & drop or click to select files'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{marginLeft: 4}}>
                      (Multiple files allowed)
                    </Typography>
                  </div>

                  {uploadedDocuments.length > 0 && (
                    <div className="upload-list">
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} className="upload-title">
                        Files ready to upload:
                      </Typography>
                      {uploadedDocuments.map((file, index) => (
                        <div key={index} className="upload-item">
                          <Typography variant="body2" className="filename">
                            {file.name} 
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => removeUploadedDocument(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

           <div className="row-2">
            {/* Coloana din stânga - HISTORY */}
            <div className="history-card">
              <div className="history-header">
                <HistoryIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h7" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  RECEPTION HISTORY
                </Typography>
              </div>

              <div className="history-content">
                {!historyLoading && Object.keys(historyByDate).length > 0 ? (
                  <div className="timeline-container">
                    {Object.entries(historyByDate).map(([dateTime, entries]) => (
                      <div key={dateTime} className="timeline-group">
                        <div className="timeline-date">
                          <div className="date-badge">
                            {dateTime.split(' - ')[0]}
                            <span className="time">{dateTime.split(' - ')[1]}</span>
                          </div>
                        </div>
                        
                        <div className="timeline-entries">
                          {entries.map((entry) => (
                            <div key={entry.id} className="timeline-entry">
                              <div className="entry-content">
                                <div className="entry-product">{entry.product_name}</div>
                                <div className="entry-quantity">
                                  {editingHistory?.id === entry.id ? (
                                    <div className="edit-mode">
                                      <NumericFormat
                                        value={editValue}
                                        onValueChange={(values) => setEditValue(values.floatValue ?? 0)}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={4}
                                        allowNegative={false}
                                        className="edit-input"
                                      />
                                      <button onClick={handleSaveHistoryEdit} className="save-btn">✓</button>
                                      <button onClick={handleCancelEdit} className="cancel-btn">✗</button>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="quantity">
                                        <NumericFormat 
                                          value={entry.qty_received} 
                                          displayType="text" 
                                          decimalScale={4}
                                          thousandSeparator="."
                                          decimalSeparator=","
                                        />
                                      </span>
                                      <button onClick={() => handleStartEdit(entry)} className="edit-btn">✎</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  !historyLoading && (
                    <div className="no-history">No reception history available</div>
                  )
                )}
              </div>
            </div>

            {/* Coloana din dreapta - DOCUMENTS și NOTES */}
            <div className="right-column">
              <div className="documents-card">
                <div className="documents-header">
                  <AttachFileIcon sx={{ color: '#1976d2' }} />
                  <Typography variant="h7" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    ATTACHED DOCUMENTS
                  </Typography>
                </div>

                <div className="documents-content">
                  {existingDocuments.length > 0 ? (
                    <div className="documents-list">
                      {existingDocuments.map((doc) => (
                        <div key={doc.id} className="document-item">
                          <DownloadIcon fontSize="small" sx={{ color: '#1976d2' }} />
                          <button
                            type="button"
                            onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                            className="document-link"
                          >
                            {doc.filename}
                          </button>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(doc.id)}
                            color="error"
                            sx={{ ml: 'auto' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" color="textSecondary" className="no-documents">
                      No documents attached yet
                    </Typography>
                  )}
                </div>
              </div>

              <div className="notes-card">
                <div className="documents-header">
                  <DescriptionIcon sx={{color: '#1976d2'}}/>
                  <Typography variant="h7" sx={{fontWeight: 600, color:'#1976d2'}}>
                    RECEPTION NOTES
                  </Typography>
                </div>
                
                <div className="document-content">
                    <TextField 
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      placeholder="Add reception notes here..."
                      value={receptionNote}
                      onChange={handleReceptionNoteChange}
                      className="reception-notes-textarea"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#eeeeee',
                          },
                        }
                      }}
                    />
                </div>
              </div>
            </div>
          </div>
          </div>
        </form>
      </div>

      <Dialog open={openDialog} onClose={handleCancel}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cannot save. Total Received (including current) is greater than Qty Ordered for one or more items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/*dialog de confirmare pentru stergere*/}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{color: '#d32f2f'}}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
            <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this document? This action cannot be undone.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({...snackbar, open: false})}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ReceptionEdit;